import { SnapshotModel } from 'src/common/api/reportsSnapshot'
import { DEFAULT_RATE } from './portalData'

// One point per calendar day of the selected window, feeding the dual-axis earnings chart.
export interface RevenueChartPoint {
  // Short "Mon D" label for the x-axis tick.
  label: string
  // Running total of gross QCAD earned across all wallets, up to and including this day. The final
  // value equals the "Gross QCAD Earned" headline metric (same snapshot math as the settlement view).
  cumulativeEarned: number
  // Net rate (annualized) on this day, in percent (e.g. 1.25 for 1.25%). null before the first known rate.
  ratePercent: number | null
}

interface RateLike {
  rate: number
  startDate?: string
  createdAt?: string
}

export interface BuildRevenueChartArgs {
  snapshots: SnapshotModel[]
  // Org rate schedule when available. Partners have no rate-schedule endpoint, so when omitted the
  // rate step-line is derived from the rate stamped on each day's snapshots instead.
  rates?: RateLike[]
  fromDate: string
  toDate: string
  // Percent rate used for days before the first known rate change. Defaults to the ledger fallback.
  fallbackRatePercent?: number
}

const DAY_MS = 86400000

const toNum = (v: unknown): number => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const isFiniteRate = (v: unknown): boolean => Number.isFinite(Number(v))

// Daily accrual for one snapshot: EOD balance × annual rate ÷ 365 (mirrors buildPortalData).
const snapshotDailyRevenue = (s: SnapshotModel, fallbackRatePercent: number): number => {
  const ratePercent = isFiniteRate(s.rate) ? Number(s.rate) : fallbackRatePercent
  return (toNum(s.balance) * (ratePercent / 100)) / 365
}

export const buildRevenueChartData = ({
  snapshots,
  rates = [],
  fromDate,
  toDate,
  fallbackRatePercent = DEFAULT_RATE * 100
}: BuildRevenueChartArgs): RevenueChartPoint[] => {
  const startDay = fromDate.slice(0, 10)
  const endDay = toDate.slice(0, 10)
  const start = Date.parse(`${startDay}T00:00:00Z`)
  const end = Date.parse(`${endDay}T00:00:00Z`)
  if (Number.isNaN(start) || Number.isNaN(end) || end < start) return []

  // Sum each day's earning across every wallet that had a snapshot that day.
  const earnedByDay = new Map<string, number>()
  snapshots.forEach((s) => {
    const day = s.consolidationDate.slice(0, 10)
    if (day < startDay || day > endDay) return
    earnedByDay.set(day, (earnedByDay.get(day) || 0) + snapshotDailyRevenue(s, fallbackRatePercent))
  })

  // Rate step-changes: prefer the org's configured schedule; otherwise fall back to the rate stamped
  // on each day's snapshots. Both collapse to (day → percent) change points.
  const rateChanges = rates.length
    ? rates
        .filter((r) => isFiniteRate(r.rate))
        .map((r) => ({ day: (r.startDate || r.createdAt || '').slice(0, 10), rate: Number(r.rate) }))
        .filter((r) => r.day)
    : Array.from(
        snapshots
          .filter((s) => {
            const day = s.consolidationDate.slice(0, 10)
            return day >= startDay && day <= endDay && isFiniteRate(s.rate)
          })
          .reduce((m, s) => {
            const day = s.consolidationDate.slice(0, 10)
            if (!m.has(day)) m.set(day, Number(s.rate))
            return m
          }, new Map<string, number>()),
        ([day, rate]) => ({ day, rate })
      )
  rateChanges.sort((a, b) => (a.day < b.day ? -1 : 1))

  // Snapshot-derived rates only exist on snapshot days, so extrapolate the earliest stamped rate back
  // to the window start; the latest rate already carries forward to the window end via rateForDay.
  // Without this the dotted rate line would start mid-chart on partner views.
  if (!rates.length && rateChanges.length && rateChanges[0].day > startDay) {
    rateChanges[0] = { ...rateChanges[0], day: startDay }
  }

  // Latest rate whose change day is on or before the given day (horizontal steps that jump on changes).
  const rateForDay = (dayKey: string): number | null => {
    let applicable: number | null = null
    for (const c of rateChanges) {
      if (c.day <= dayKey) applicable = c.rate
      else break
    }
    return applicable
  }

  const points: RevenueChartPoint[] = []
  let cumulative = 0
  for (let ts = start; ts <= end; ts += DAY_MS) {
    const dayKey = new Date(ts).toISOString().slice(0, 10)
    cumulative += earnedByDay.get(dayKey) || 0
    points.push({
      label: new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      cumulativeEarned: cumulative,
      ratePercent: rateForDay(dayKey)
    })
  }
  return points
}
